/**
 * useCredits Hook - Provides access to user credit balance and operations
 * Integrates with user profile system for credit management
 */

import { useState, useCallback, useEffect } from 'react';
import { useUserProfile } from './useUserProfile';
import type { CreditOperation } from '../types/credits';

/**
 * Credits API service for backend communication
 */
class CreditsAPIService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_CREDITS_URL || '';
  }

  /**
   * Get authorization header with token
   */
  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Consume credits for an operation
   */
  async consumeCredits(amount: number, operation: CreditOperation): Promise<number> {
    const response = await fetch(`${this.baseUrl}/use`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify({ amount, operation }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to consume credits');
    }

    const data = await response.json();
    return data.remainingCredits;
  }

  /**
   * Get current credit balance
   */
  async getBalance(): Promise<number> {
    const response = await fetch(this.baseUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to fetch credit balance');
    }

    const data = await response.json();
    return data.credits;
  }
}

// Singleton instance
const creditsAPI = new CreditsAPIService();

/**
 * Insufficient credits error class
 */
export class InsufficientCreditsError extends Error {
  constructor(required: number, available: number) {
    super(`Insufficient credits: required ${required}, available ${available}`);
    this.name = 'InsufficientCreditsError';
  }
}

/**
 * Return type for useCredits hook
 */
export interface UseCreditsReturn {
  /** Current credit balance */
  credits: number;
  /** Loading state for credit operations */
  isLoading: boolean;
  /** Error state for credit operations */
  error: Error | null;
  /** Refresh credit balance from backend */
  refreshCredits: () => Promise<void>;
  /** Consume credits for an operation */
  consumeCredit: (operation: CreditOperation, amount?: number) => Promise<number>;
  /** Check if user has sufficient credits */
  hasCredits: (required: number) => boolean;
}

/**
 * Custom hook for managing user credits
 * Provides credit balance and operations integrated with user profile
 * 
 * Features:
 * - Extract credits from user profile
 * - Refresh credit balance
 * - Consume credits with validation
 * - Check credit availability
 * - Optimistic updates for better UX
 * - Automatic sync with user profile
 * 
 * Must be used within UserProfileProvider component tree
 * 
 * @returns Credit balance and operations
 * 
 * @example
 * ```tsx
 * function GenerateButton() {
 *   const { credits, consumeCredit, hasCredits, isLoading } = useCredits();
 * 
 *   const handleGenerate = async () => {
 *     if (!hasCredits(1)) {
 *       alert('Insufficient credits');
 *       return;
 *     }
 * 
 *     try {
 *       const remaining = await consumeCredit('generate');
 *       console.log(`Credits remaining: ${remaining}`);
 *     } catch (error) {
 *       console.error('Failed to consume credit:', error);
 *     }
 *   };
 * 
 *   return (
 *     <button onClick={handleGenerate} disabled={!hasCredits(1) || isLoading}>
 *       Generate (1 credit)
 *     </button>
 *   );
 * }
 * ```
 */
export function useCredits(): UseCreditsReturn {
  const { profile, refreshProfile } = useUserProfile();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Local state for optimistic updates
  const [optimisticCredits, setOptimisticCredits] = useState<number | null>(null);

  // Extract credits from profile, use optimistic value if available
  const credits = optimisticCredits ?? profile?.currentCredits ?? 0;

  /**
   * Refresh credit balance from backend
   * Syncs with user profile to get latest credit data
   */
  const refreshCredits = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setOptimisticCredits(null); // Clear optimistic state

    try {
      // Refresh user profile which includes credit balance
      await refreshProfile();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to refresh credits');
      setError(error);
      console.error('Error refreshing credits:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [refreshProfile]);

  /**
   * Consume credits for an operation
   * Updates local state optimistically and syncs with backend
   * 
   * @param operation - Type of operation consuming credits
   * @param amount - Number of credits to consume (default: 1)
   * @returns Remaining credit balance
   * @throws InsufficientCreditsError if user doesn't have enough credits
   */
  const consumeCredit = useCallback(async (
    operation: CreditOperation,
    amount: number = 1
  ): Promise<number> => {
    // Check if user has sufficient credits
    const currentCredits = optimisticCredits ?? profile?.currentCredits ?? 0;
    if (currentCredits < amount) {
      const error = new InsufficientCreditsError(amount, currentCredits);
      setError(error);
      throw error;
    }

    setIsLoading(true);
    setError(null);

    // Optimistic update - immediately update local state
    const optimisticBalance = currentCredits - amount;
    setOptimisticCredits(optimisticBalance);

    try {
      // Call backend to consume credits
      const remainingCredits = await creditsAPI.consumeCredits(amount, operation);
      
      // Update optimistic state with actual value from backend
      setOptimisticCredits(remainingCredits);
      
      // Sync with user profile to update all credit-related data
      // Wait for profile refresh to complete so UI updates immediately
      await refreshProfile();

      return remainingCredits;
    } catch (err) {
      // Revert optimistic update on error
      setOptimisticCredits(null);
      
      const error = err instanceof Error ? err : new Error('Failed to consume credits');
      setError(error);
      console.error('Error consuming credits:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [profile?.currentCredits, optimisticCredits, refreshProfile]);

  /**
   * Check if user has sufficient credits
   * 
   * @param required - Number of credits required
   * @returns True if user has enough credits
   */
  const hasCredits = useCallback((required: number): boolean => {
    const currentCredits = optimisticCredits ?? profile?.currentCredits ?? 0;
    return currentCredits >= required;
  }, [profile?.currentCredits, optimisticCredits]);

  /**
   * Clear optimistic state when profile updates
   * This ensures we use the actual profile data once it's loaded
   */
  useEffect(() => {
    if (profile?.currentCredits !== undefined && optimisticCredits !== null) {
      // If profile has updated and we have optimistic state,
      // clear optimistic state after a short delay to allow for smooth transition
      const timer = setTimeout(() => {
        setOptimisticCredits(null);
      }, 500);
      
      return () => clearTimeout(timer);
    }
    
    return undefined;
  }, [profile?.currentCredits, optimisticCredits]);

  return {
    credits,
    isLoading,
    error,
    refreshCredits,
    consumeCredit,
    hasCredits,
  };
}
